import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Pagination,
  Spinner,
  Table,
} from "flowbite-react";
import { FaArrowsLeftRight } from "react-icons/fa6";
import axios from "../../config/axiosInstance";
import { useQuery } from "react-query";
import {
  ADMIN,
  CATEGORIES,
  COMPANY,
  REPORTS,
  USERS,
} from "../../config/apiConfig";
import { formatToReal, isOlderThan30Days } from "../../utils/utils";
import { IoMdSearch } from "react-icons/io";
import EmptyTable from "./EmptyTable";
import {
  MdOutlineAttachMoney,
  MdOutlinePageview,
  MdPageview,
} from "react-icons/md";
import { BsFillBarChartFill } from "react-icons/bs";
import DrawerLoading from "../DrawerLoading";
import TableLoading from "../TableLoading";
import { useApplication } from "../../contexts/ApplicationContext";
import CustomCreditQueryReview from "../queries/CustomCreditQueryReview";
import CustomVehicleQueryReview from "../queries/CustomVehicleQueryReview";
import { CgDetailsMore } from "react-icons/cg";
import { Tooltip } from "react-tooltip";
import { useReactToPrint } from "react-to-print";
import { FaArrowRight, FaFilter, FaMoneyBillWave } from "react-icons/fa";
import { LuFilter, LuFilterX } from "react-icons/lu";
import AutoCompleteInput from "../inputs/Autocomplete";
import CustomCadastralQueryReview from "../queries/CustomCadastralQueryReview";

const ReportTable = () => {
  const { user, userPermission } = useApplication();
  const [activeTab, setActiveTab] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPriceSum, setTotalPriceSum] = useState(0);
  const [totalCostPrice, setTotalCostPrice] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const onPageChange = (page) => setCurrentPage(page);
  const [searchName, setSearchName] = useState("");
  const [nameParam, setNameParam] = useState("");
  const [selectedQueries, setSelectedQueries] = useState([]);
  const [listCategoryQueries, setListCategoryQueries] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [dataQuery, setDataQuery] = useState(null);
  const [selectedQuery, setSelectedQuery] = useState(0);
  const [selectedQueryReview, setSelectedQueryReview] = useState(null);
  const handleCloseReviewModal = () => {
    setOpenModal(false);
    setSelectedQueryReview(null);
    setDataQuery(null);
  };
  const printRef = useRef();
  const reactToPrintFn = useReactToPrint({ contentRef: printRef });
  const [showFilters, setShowFilters] = useState(true);
  const [renderFilters, setRenderFilters] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState({
    label: "Todos os produtos",
    value: 0,
  });
  const [selectedUserId, setSelectedUserId] = useState("");

  useEffect(() => {
    if (userPermission === "SELLER" || userPermission === "USER") {
      setSelectedUserId(user?.id);
    }
  }, [userPermission]);

  useEffect(() => {
    if (showFilters) {
      setRenderFilters(true); // mostra o conteúdo imediatamente
    } else {
      // espera a animação terminar antes de esconder
      const timeout = setTimeout(() => setRenderFilters(false), 500); // mesmo tempo da transition
      return () => clearTimeout(timeout);
    }
  }, [showFilters]);

  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const {
    isLoading: isLoadingQueryPermissions,
    error: errorQueryPermissions,
    data: userQueryPermissions,
  } = useQuery(["userQueryPermissions"], async () => {
    const { data: items } = await axios.get(
      `${USERS.GET_USER_QUERY_PERMISSIONS}`
    );

    return items?.data?.map((query) => query.id);
  });

  const {
    isLoading: isLoadingUsers,
    error: errorUsers,
    data: users,
  } = useQuery(["users"], async () => {
    const { data: items } = await axios.get(
      `${USERS.GET_USERS}?itemsPerPage=-1`
    );

    return items.data.data;
  });

  const {
    isLoading: isLoadingCategories,
    error: errorCategories,
    data: categories,
  } = useQuery(["categories", currentPage], async () => {
    const { data } = await axios.get(`${CATEGORIES.GET_ALL}`);

    return data.data.data;
  });

  const {
    isLoading: isLoadingQueries,
    error: errorQueries,
    data: listQueries,
  } = useQuery(["listQueries"], async () => {
    try {
      const { data } = await axios.get(`${COMPANY.GET_PERMISSION_QUERIES}`);

      if (!data.data) {
        throw new Error("Erro ao buscar produtos");
      }

      let customers = data.data.map((customer) => ({
        label: customer.name,
        value: customer.QueryId,
      }));

      customers = [{ label: "Todos os produtos", value: 0 }, ...customers];

      return customers;
    } catch (error) {
      throw new Error(`Erro ao buscar produtos: ${error.message}`);
    }
  });

  const {
    isLoading: isLoadingReportQueries,
    error: errorListReportQueries,
    data: listReportQueries,
  } = useQuery(
    [
      "listReportQueries",
      startDate,
      endDate,
      currentPage,
      selectedQueries,
      selectedStatuses,
      nameParam,
      selectedProduct,
      selectedUserId,
    ],
    async () => {
      const { data } = await axios.get(
        `${REPORTS.GET_COMPANY_QUERIES_HISTORY}?startDate=${startDate}&endDate=${endDate}&page=${currentPage}&status=${selectedStatuses}&search=${nameParam}&queries=${selectedProduct?.value}&userId=${selectedUserId}`
      );

      setTotalPages(data.data.totalPages);
      setTotalCostPrice(data.data.totalCostPrice);
      setTotalPriceSum(data.data.totalPriceSum);
      setTotalCount(data.data.totalCount);

      return data.data.data;
    }
  );

  const {
    isLoading: isLoadingReportSimplifiedQueries,
    error: errorListReportSimplifiedQueries,
    data: listReportSimplifiedQueries,
  } = useQuery(
    [
      "listReportSimplifiedQueries",

      startDate,
      endDate,
      currentPage,
      selectedQueries,
      selectedStatuses,
      selectedProduct,
      selectedUserId,
    ],
    async () => {
      const { data } = await axios.get(
        `${REPORTS.GET_COMPANY_QUERIES_HISTORY_SIMPLIFIED}?startDate=${startDate}&endDate=${endDate}&page=${currentPage}&status=${selectedStatuses}&isModular=false&queries=${selectedProduct?.value}&userId=${selectedUserId}`
      );

      setTotalPages(data.data.totalPages);
      setTotalPriceSum(data.data.totalSumPrice || 0);
      setTotalCostPrice(data.data.totalCostPrice);
      setTotalCount(data.data.totalSumCount);

      return data.data.data;
    },
    {
      enabled: activeTab === 1,
    }
  );

  const handleCheckboxChange = (status, setState) => {
    setState((prev) =>
      prev.includes(status)
        ? prev.filter((item) => item !== status)
        : [...prev, status]
    );
  };

  const handleSearchName = (e) => {
    e.preventDefault();
    setNameParam(searchName);
  };

  const appliedFilterSummary = useMemo(() => {
    const items = [];
    if (selectedProduct?.value && selectedProduct?.value !== 0) {
      items.push(`Produto: ${selectedProduct?.label || "-"}`);
    }
    if (startDate || endDate) {
      const startLabel = startDate
        ? moment(startDate).format("DD/MM/YYYY")
        : "—";
      const endLabel = endDate ? moment(endDate).format("DD/MM/YYYY") : "—";
      items.push(`Período: ${startLabel} a ${endLabel}`);
    }
    if (selectedStatuses.length > 0) {
      const labels = selectedStatuses
        .map((status) => (status === 1 ? "Sucesso" : "Falha"))
        .join(", ");
      items.push(`Status: ${labels}`);
    }
    if (selectedUserId) {
      const userLabel =
        users?.find((item) => String(item.id) === String(selectedUserId))
          ?.name || "Usuário selecionado";
      items.push(`Usuário: ${userLabel}`);
    }
    if (nameParam) {
      items.push(`Busca: ${nameParam}`);
    }
    return items;
  }, [
    endDate,
    nameParam,
    selectedProduct?.label,
    selectedProduct?.value,
    selectedStatuses,
    selectedUserId,
    startDate,
    users,
  ]);

  const hasActiveFilters = appliedFilterSummary.length > 0;

  useEffect(() => {
    setCurrentPage(1);
  }, [
    activeTab,
    startDate,
    endDate,
    selectedProduct,
    selectedStatuses,
    selectedUserId,
  ]);

  useEffect(() => {
    setListCategoryQueries(() =>
      categories?.map((category) => {
        return {
          ...category,
          Queries: category?.Queries?.filter((query) =>
            userQueryPermissions?.includes(query.id)
          ),
        };
      })
    );
  }, [userQueryPermissions, categories]);

  return (
    <div className="relative">
      <div className="flex justify-between items-center mb-2">
        <Button
          size="xs"
          onClick={() => setShowFilters((prev) => !prev)}
          className="bg-white text-slate-700 border border-slate-200 shadow-sm hover:bg-gray-50 hover:text-white transition-all duration-300"
        >
          <div className="flex items-center gap-2 transition-all duration-300">
            <span>{showFilters ? "Esconder filtros" : "Mostrar filtros"}</span>
            <span className={`transition-transform duration-300 `}>
              {!showFilters ? <LuFilter /> : <LuFilterX />}
            </span>
          </div>
        </Button>
      </div>
      <div className="flex flex-col gap-4">
        {showFilters && (
          <div className="transition-all duration-500 ease-in-out bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            {(isLoadingCategories || isLoadingQueryPermissions) && (
              <DrawerLoading />
            )}

            <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-slate-200 bg-gray-50">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Filtros Avançados
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-6 gap-3 px-3 py-3 items-start">
              {/* Produto */}
              <div className="flex flex-col md:col-span-3">
                <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                  Produto
                </label>
                <div className="h-9">
                  <AutoCompleteInput
                    options={listQueries}
                    label=""
                    onChange={(product) => setSelectedProduct(product)}
                    value={selectedProduct?.value}
                    isLoading={isLoadingQueries}
                    className="h-full"
                  />
                </div>
                {errorQueries && (
                  <Alert color="failure" className="mt-2">
                    Ocorreu um erro ao buscar clientes
                  </Alert>
                )}
              </div>
              {/* Período */}
              <div className="flex flex-col md:col-span-3 mt-3 md:mt-0">
                <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                  Período da consulta
                </label>
                <div className="flex gap-2 items-center h-12">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      if (moment(e.target.value).isAfter(moment(endDate))) {
                        setEndDate(e.target.value);
                      }
                    }}
                    className="w-full h-full bg-white border border-slate-300 text-xs rounded-md px-2.5 py-2 focus:ring-slate-500 focus:border-slate-500"
                  />
                  <span className="text-[11px] text-slate-500">até</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate}
                    className="w-full h-full bg-white border border-slate-300 text-xs rounded-md px-2.5 py-2 focus:ring-slate-500 focus:border-slate-500"
                  />
                </div>
              </div>

              {/* Status */}
              <div className="flex flex-col md:col-span-3">
                <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                  Status da Consulta
                </label>
                <div className="flex gap-2 h-9">
                  <button
                    type="button"
                    onClick={() => handleCheckboxChange(1, setSelectedStatuses)}
                    className={`h-full px-3 rounded-md text-xs font-semibold border transition w-full ${
                      selectedStatuses.includes(1)
                        ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                        : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    Sucesso
                  </button>
                  <button
                    type="button"
                    onClick={() => handleCheckboxChange(0, setSelectedStatuses)}
                    className={`h-full px-3 rounded-md text-xs font-semibold border transition w-full ${
                      selectedStatuses.includes(0)
                        ? "bg-rose-600 text-white border-rose-600 shadow-sm"
                        : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    Falha
                  </button>
                </div>
              </div>
              {userPermission === "ADMIN" ||
                (userPermission === "POWER_USER" && (
                  <div className="w-full md:col-span-3">
                    <label className="block text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                      Usuário
                    </label>
                    <div className="flex gap-2">
                      <select
                        value={selectedUserId}
                        onChange={(e) => setSelectedUserId(e.target.value)}
                        className="w-full h-9 bg-white border border-slate-300 text-xs rounded-md px-2.5 py-2 focus:ring-slate-500 focus:border-slate-500"
                      >
                        <option value="">Todos os usuários</option>
                        {(users || []).map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.name} {u.email ? `— ${u.email}` : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        <div className="flex-1">
          <div className="mt-3">
            <div className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-gradient-to-r from-white to-gray-50 px-4 py-2 shadow-sm md:flex-row md:items-center md:justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 shadow-inner">
                  <BsFillBarChartFill className="text-xl" />
                </div>
                <p className="text-base font-semibold text-gray-900">
                  Resultado da busca
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2 justify-end text-xs">
                {hasActiveFilters ? (
                  <>
                    <span className="uppercase tracking-wide text-gray-500">
                      Filtros ativos
                    </span>
                    {appliedFilterSummary.slice(0, 4).map((item, index) => (
                      <span
                        key={`${item}-${index}`}
                        className="px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-medium border border-gray-200 shadow-sm"
                      >
                        {item}
                      </span>
                    ))}
                    {appliedFilterSummary.length > 4 && (
                      <span className="px-2 py-1 rounded-full bg-gray-50 text-gray-500 text-xs font-medium border border-dashed border-gray-300">
                        +{appliedFilterSummary.length - 4} oculto(s)
                      </span>
                    )}
                  </>
                ) : (
                  <span className="uppercase tracking-wide text-gray-500">
                    Nenhum filtro aplicado
                  </span>
                )}
              </div>
            </div>
          </div>
          {startDate && endDate && (
            <div className="italic text-sm mb-2 flex items-center gap-2">
              Informações baseadas no período de{" "}
              <Badge className="w-fit">
                {moment(startDate).format("DD/MM/yyyy")}
              </Badge>{" "}
              até{" "}
              <Badge className="w-fit">
                {" "}
                {moment(endDate).format("DD/MM/yyyy")}
              </Badge>
            </div>
          )}

          <div className="overflow-x-auto mt-4 mb-4">
            <div className="flex gap-4 min-w-[900px] pb-2">
              {/* Valor Total */}
              {/* <div className="min-w-[260px] flex-1 rounded-2xl p-4 shadow-md text-white bg-gradient-to-br from-[#1E429F] to-[#4F46E5] relative">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm opacity-90">Valor Total</p>
                    <h2 className="text-2xl font-bold mt-1">
                      {!isLoadingReportQueries ? (
                        formatToReal(totalPriceSum)
                      ) : (
                        <div className="animate-pulse w-32 h-6 bg-white/30 rounded" />
                      )}
                    </h2>
                  </div>
                  <MdOutlineAttachMoney className="text-white text-4xl opacity-20" />
                </div>
              </div> */}

              {/* Total de Consultas */}
              <div className="min-w-[260px] flex-1 rounded-2xl p-4 shadow-md text-white bg-gradient-to-br from-[#0F766E] to-[#14B8A6] relative">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm opacity-90">Total de Consultas</p>
                    <h2 className="text-2xl font-bold mt-1">
                      {!isLoadingReportQueries ? (
                        totalCount
                      ) : (
                        <div className="animate-pulse w-24 h-6 bg-white/30 rounded" />
                      )}
                    </h2>
                  </div>
                  <BsFillBarChartFill className="text-white text-4xl opacity-20" />
                </div>
              </div>
              {userPermission === "POWER_USER" && (
                <div className="min-w-[260px] flex-1 rounded-2xl p-4 shadow-md text-white bg-gradient-to-br from-[#0F766E] to-[#14B8A6] relative">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm opacity-90">
                        Custo total das consultas
                      </p>
                      <h2 className="text-2xl font-bold mt-1">
                        {!isLoadingReportQueries ? (
                          totalCostPrice
                        ) : (
                          <div className="animate-pulse w-24 h-6 bg-white/30 rounded" />
                        )}
                      </h2>
                    </div>
                    <BsFillBarChartFill className="text-white text-4xl opacity-20" />
                  </div>
                </div>
              )}

              {/* VCM */}
              {user?.Company?.typePayment === "POS" && (
                <div className="min-w-[260px] flex-1 rounded-2xl p-4 shadow-md text-white bg-gradient-to-br from-[#B45309] to-[#F59E0B] relative">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm opacity-90">
                        Consumação Mínima (VCM)
                      </p>
                      <h2 className="text-2xl font-bold mt-1">
                        {user?.Company?.vcm
                          ? formatToReal(user?.Company?.vcm)
                          : "-"}
                      </h2>
                    </div>
                    <MdOutlineAttachMoney className="text-white text-4xl opacity-20" />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mb-4 flex justify-end items-center gap-2">
            <div className="text-xs font-semibold">Visualizar relatório:</div>
            <ul className="text-sm rounded-md border border-gray-300 flex">
              <li
                className={`py-2 px-4 rounded-md cursor-pointer ${
                  activeTab === 0
                    ? `bg-primary text-white font-semibold shadow-md`
                    : `bg-white`
                }`}
                onClick={() => setActiveTab(0)}
              >
                Detalhado
              </li>
              <li
                className={`py-2 px-4 rounded-md cursor-pointer ${
                  activeTab === 1
                    ? `bg-primary text-white font-semibold shadow-md`
                    : `bg-white`
                }`}
                onClick={() => setActiveTab(1)}
              >
                Simplificado
              </li>
            </ul>
          </div>
          {activeTab === 0 && (
            <form
              className="flex gap-1 mt-0 w-full pl-3 pr-2 py-2 md:py-1 rounded-md shadow-sm bg-gray-200"
              onSubmit={handleSearchName}
            >
              <input
                type="text"
                placeholder="Busque por parâmetro da consulta..."
                className="border-0 py-1 text-sm mt-1 focus:outline-none focus:ring-0 focus:border-gray-300 px-0 w-full bg-gray-200 placeholder:text-gray-700"
                onChange={(e) => setSearchName(e.target.value)}
                value={searchName}
              />
              <Button
                size="sm"
                className="flex items-center bg-secondary w-20"
                type="submit"
              >
                <IoMdSearch />
              </Button>
            </form>
          )}

          {activeTab === 0 ? (
            <>
              {listReportQueries?.length > 0 || isLoadingReportQueries ? (
                <>
                  <div className="md:hidden space-y-2 mt-3">
                    {!isLoadingReportQueries ? (
                      listReportQueries?.map((query, index) => {
                        const created = moment(query.createdAt);
                        return (
                          <div
                            key={index}
                            className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                                  <span
                                    className="truncate"
                                    title={query.queryName}
                                  >
                                    {query.queryName}
                                  </span>
                                  {query?.hasIA ? (
                                    <span className="text-[10px] uppercase tracking-wide bg-blue-100 text-blue-700 rounded-full px-2 py-0.5">
                                      +IA
                                    </span>
                                  ) : null}
                                </div>
                                <div className="mt-0.5 text-[11px] text-gray-500 flex flex-wrap items-center gap-2">
                                  <span className="font-semibold text-gray-700">
                                    {created.format("DD/MM/YY")}
                                  </span>
                                  <span>{created.format("HH:mm")}h</span>
                                  <span className="text-gray-300">•</span>
                                  <span className="truncate">
                                    {query.parameter || "-"}
                                  </span>
                                </div>
                              </div>
                              <span
                                className={`flex items-center justify-center rounded-full text-[10px] px-2 py-0.5 font-semibold ${
                                  query.isSuccess
                                    ? "bg-emerald-100 text-emerald-700"
                                    : "bg-rose-100 text-rose-700"
                                }`}
                              >
                                {query.isSuccess ? "Sucesso" : "Falha"}
                              </span>
                            </div>

                            <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-gray-600">
                              <span className="flex items-center gap-1">
                                <span className="font-semibold text-gray-700">
                                  Usuário:
                                </span>
                                <span className="truncate max-w-[200px]">
                                  {query.userName || "-"}
                                </span>
                              </span>
                              {user?.Company?.showPriceQuery && (
                                <span className="flex items-center gap-1 font-semibold text-gray-700">
                                  <FaMoneyBillWave
                                    size={12}
                                    className="text-green-600"
                                  />
                                  {formatToReal(query?.price || 0)}
                                </span>
                              )}
                            </div>

                            <div className="mt-2 flex items-center justify-end gap-1 text-gray-600">
                              {query?.isSuccess ? (
                                <button
                                  type="button"
                                  className="p-1 rounded-full border border-gray-200 text-gray-600 hover:border-gray-300 transition"
                                  title="Visualizar consulta"
                                  onClick={() => {
                                    setSelectedQueryReview(query);
                                    setDataQuery(query.data);
                                    setOpenModal(true);
                                  }}
                                >
                                  <MdOutlinePageview fontSize={16} />
                                </button>
                              ) : null}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="p-1 space-y-2">
                        {Array.from({ length: 10 }).map((_, i) => (
                          <div
                            key={i}
                            className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm"
                          >
                            <div className="flex items-center gap-2">
                              <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
                              <div className="h-4 w-14 bg-gray-200 rounded-full animate-pulse" />
                            </div>
                            <div className="mt-2 h-3 w-3/4 bg-gray-200 rounded animate-pulse" />
                            <div className="mt-3 h-3 w-1/2 bg-gray-200 rounded animate-pulse" />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="hidden md:block overflow-x-auto rounded-2xl border border-slate-200 bg-white/90 shadow-sm mt-5">
                    <Table
                      striped
                      style={{ fontSize: "13px" }}
                      className="min-w-[900px]"
                    >
                      <Table.Head className="bg-gradient-to-b from-slate-100 to-slate-200/70">
                        <Table.HeadCell className="text-center bg-transparent text-[11px] uppercase tracking-wide text-slate-600 font-semibold">
                          Data
                        </Table.HeadCell>
                        <Table.HeadCell className="bg-transparent text-[11px] uppercase tracking-wide text-slate-600 font-semibold">
                          Consulta
                        </Table.HeadCell>
                        {user?.Company?.showPriceQuery && (
                          <Table.HeadCell className="text-center bg-transparent text-[11px] uppercase tracking-wide text-slate-600 font-semibold">
                            Valor
                          </Table.HeadCell>
                        )}
                        <Table.HeadCell className="text-center bg-transparent text-[11px] uppercase tracking-wide text-slate-600 font-semibold">
                          Usuário
                        </Table.HeadCell>
                        <Table.HeadCell className="text-center bg-transparent text-[11px] uppercase tracking-wide text-slate-600 font-semibold">
                          Status
                        </Table.HeadCell>
                        <Table.HeadCell className="text-center bg-transparent text-[11px] uppercase tracking-wide text-slate-600 font-semibold">
                          Ações
                        </Table.HeadCell>
                      </Table.Head>

                      <Table.Body className="divide-y divide-slate-200/70">
                        {!isLoadingReportQueries ? (
                          <>
                            {listReportQueries?.length > 0 ? (
                              listReportQueries.map((query, index) => (
                                <Table.Row
                                  key={index}
                                  className="bg-white even:bg-slate-50/40 hover:bg-slate-100/60 transition-colors text-sm text-slate-800"
                                >
                                  {/* Data */}
                                  <Table.Cell className="text-center py-2 px-4 font-medium text-sm text-slate-700 leading-tight">
                                    <div className="inline-flex flex-col items-center gap-1">
                                      <span className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold text-slate-700">
                                        {moment(query.createdAt).format(
                                          "DD/MM/yyyy"
                                        )}
                                      </span>
                                      <span className="text-[11px] text-slate-500">
                                        {moment(query.createdAt).format(
                                          "HH:mm"
                                        )}
                                        h
                                      </span>
                                    </div>
                                  </Table.Cell>

                                  {/* Nome da consulta + parâmetro */}
                                  <Table.Cell className="py-2 px-4">
                                    <div className="flex items-center gap-2 font-semibold text-slate-900">
                                      <span className="truncate">
                                        {query.queryName}
                                      </span>
                                      {query?.hasIA ? (
                                        <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700">
                                          + IA
                                        </span>
                                      ) : null}
                                    </div>
                                    <div className="text-[11px] text-slate-500">
                                      Parâmetro: {query.parameter}
                                    </div>
                                  </Table.Cell>

                                  {user?.Company?.showPriceQuery && (
                                    <Table.Cell className="text-center py-3 px-4">
                                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-100">
                                        <FaMoneyBillWave
                                          size={14}
                                          className="text-emerald-600"
                                        />
                                        {formatToReal(query?.price || 0)}
                                      </span>
                                    </Table.Cell>
                                  )}

                                  {/* Nome do usuário */}
                                  <Table.Cell
                                    className="text-center py-2 px-4 max-w-[180px] truncate"
                                    title={query.userName}
                                  >
                                    <div className="inline-flex max-w-[180px] items-center gap-2 truncate">
                                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-[11px] font-semibold text-slate-700">
                                        {query.userName?.[0] || "?"}
                                      </span>
                                      <span className="truncate font-medium text-slate-800">
                                        {query.userName}
                                      </span>
                                    </div>
                                  </Table.Cell>

                                  {/* Sucesso ou falha */}
                                  <Table.Cell className="text-center py-2 px-4">
                                    <Badge
                                      color={query.isSuccess ? "green" : "red"}
                                      className="mx-auto w-fit rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ring-inset ring-slate-200/70"
                                    >
                                      <span className="inline-flex items-center gap-1">
                                        <span
                                          className={`h-1.5 w-1.5 rounded-full ${
                                            query.isSuccess
                                              ? "bg-emerald-500"
                                              : "bg-rose-500"
                                          }`}
                                        />
                                        {query.isSuccess ? "Sucesso" : "Falha"}
                                      </span>
                                    </Badge>
                                  </Table.Cell>

                                  {/* Ações */}
                                  <Table.Cell className="text-center py-2 px-4">
                                    <div className="flex items-center justify-center gap-2">
                                      {query?.isSuccess ? (
                                        <>
                                          <Tooltip
                                            id="tooltip-view"
                                            content="Visualizar consulta"
                                          />
                                          <button
                                            type="button"
                                            data-tooltip-id="tooltip-view"
                                            className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-slate-50 p-1.5 text-blue-700 shadow-sm transition hover:border-blue-200 hover:bg-blue-50"
                                            onClick={() => {
                                              setSelectedQueryReview(query);
                                              setDataQuery(query.data);
                                              setOpenModal(true);
                                            }}
                                            aria-label="Visualizar consulta"
                                          >
                                            <MdOutlinePageview fontSize={20} />
                                          </button>
                                        </>
                                      ) : null}
                                    </div>
                                  </Table.Cell>
                                </Table.Row>
                              ))
                            ) : (
                              <Table.Row>
                                <Table.Cell
                                  colSpan={6}
                                  className="text-center py-6 text-gray-500"
                                >
                                  Nenhuma consulta encontrada neste período.
                                </Table.Cell>
                              </Table.Row>
                            )}
                          </>
                        ) : (
                          <TableLoading cols={6} />
                        )}
                      </Table.Body>
                    </Table>
                  </div>
                </>
              ) : (
                <EmptyTable icon="search_off">
                  Nenhum registro encontrado com esses filtros
                </EmptyTable>
              )}
            </>
          ) : (
            <>
              {listReportSimplifiedQueries?.length > 0 ||
              isLoadingReportSimplifiedQueries ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 bg-white/90 p-3 rounded-2xl border border-slate-200 shadow-sm">
                  {listReportSimplifiedQueries?.map((query, index) => (
                    <div
                      key={index}
                      className="flex flex-col justify-between gap-2 p-3 rounded-xl border border-slate-100 bg-gradient-to-b from-white to-slate-50/70 shadow-sm transition hover:shadow-md"
                    >
                      {/* Nome da Consulta */}
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex min-w-0 items-center gap-2">
                          <div className="flex-shrink-0 h-7 w-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[11px] font-bold uppercase shadow-sm">
                            {query.queryName?.[0]}
                          </div>
                          <div className="min-w-0">
                            <p
                              className="text-[13px] font-semibold text-slate-800 truncate"
                              title={query.queryName}
                            >
                              {query.queryName}
                            </p>
                          </div>
                        </div>
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                          Consulta
                        </span>
                      </div>

                      {/* Info quantitativa */}
                      <div className="flex items-center justify-between gap-2 text-sm">
                        <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-2.5 py-1 text-[11px] font-semibold text-blue-700 ring-1 ring-inset ring-blue-100">
                          <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
                          {query.count} Qtd.
                        </div>
                        <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-100">
                          <FaMoneyBillWave
                            size={12}
                            className="text-emerald-600"
                          />
                          {formatToReal(query?.totalPrice || 0)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyTable icon="search_off">
                  Nenhum registro encontrado com esses filtros
                </EmptyTable>
              )}
            </>
          )}

          {totalPages > 1 && (
            <div className="flex overflow-x-auto justify-center mt-3">
              <Pagination
                layout="pagination"
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={onPageChange}
                previousLabel=""
                nextLabel=""
                showIcons
              />
            </div>
          )}
        </div>
        <Modal show={openModal} onClose={handleCloseReviewModal} size="6xl">
          <ModalHeader className="py2">
            <div className="text-base mt-1">Visualizar consulta</div>
          </ModalHeader>
          <ModalBody className="bg-gray-100">
            {!isOlderThan30Days(selectedQueryReview?.createdAt) ? (
              <>
                <div className="review-query" ref={printRef}>
                  {selectedQueryReview?.categoryId === 1 && (
                    <CustomCreditQueryReview
                      key={`credit-${selectedQueryReview?.id || "none"}`}
                      dataFromLocalStorage={dataQuery}
                      query={selectedQueryReview}
                    />
                  )}
                  {selectedQueryReview?.categoryId === 2 && (
                    <CustomCadastralQueryReview
                      key={`cadastral-${selectedQueryReview?.id || "none"}`}
                      dataFromLocalStorage={dataQuery}
                      query={selectedQueryReview}
                    />
                  )}
                  {selectedQueryReview?.categoryId === 3 && (
                    <CustomVehicleQueryReview
                      key={`vehicle-${selectedQueryReview?.id || "none"}`}
                      dataFromLocalStorage={dataQuery}
                      query={selectedQueryReview}
                    />
                  )}
                </div>
              </>
            ) : (
              <div className="bg-gray-50 p-2 rounded-md border border-gray-300 rounded-md">
                A consulta não está mais disponível para visualização em nossa
                base de dados, possivelmente por ter ultrapassado o período de
                retenção (7 dias) ou por políticas de conformidade. Se precisar
                reavaliar, recomendamos gerar uma nova consulta.
              </div>
            )}
          </ModalBody>
          <ModalFooter className="flex justify-center py-2 pb-3">
            <Button onClick={() => reactToPrintFn()}>Imprimir</Button>
            <Button color="gray" onClick={handleCloseReviewModal}>
              Fechar
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </div>
  );
};

export default ReportTable;
